import { Alert, HelpText } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { showSlideoutMenu } from '@mm/state/actions/viewActions';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { openUniversalUpgradeLandmineModal } from '@mm/state/modules/modalsState/actions';
import { selectWidgetPlatformsMap } from '@mm/state/selectors/envSelectors';
import {
  combinedFeatureFlagsSelector,
  companyFeatureFlagsSelector,
  getPlanFromBillingType,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import {
  getTwilioAvailable,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as AttachmentIcon } from 'assets/attachment-icon.svg';
import { ReactComponent as AudioIcon } from 'assets/audio-widget.svg';
import { ReactComponent as CirclePlusIcon } from 'assets/circle-plus.svg';
import { ReactComponent as GifIcon } from 'assets/gif-icon.svg';
import { ReactComponent as GoogleSheetsIcon } from 'assets/google-sheets-icon.svg';
import { ReactComponent as AttributeIcon } from 'assets/icons/widgets/attribute.svg';
import { ReactComponent as ConnectionIcon } from 'assets/icons/widgets/connection.svg';
import { ReactComponent as DelayIcon } from 'assets/icons/widgets/delay.svg';
import { ReactComponent as FormIcon } from 'assets/icons/widgets/form.svg';
import { ReactComponent as GalleryIcon } from 'assets/icons/widgets/gallery.svg';
import { ReactComponent as NotificationIcon } from 'assets/icons/widgets/notification.svg';
import { ReactComponent as OtnIcon } from 'assets/icons/widgets/otn.svg';
import { ReactComponent as SmsOptInIcon } from 'assets/icons/widgets/sms_opt_in.svg';
import { ReactComponent as ImageUploadIcon } from 'assets/image-upload-icon.svg';
import { ReactComponent as NavigateIcon } from 'assets/navigate-icon.svg';
import { ReactComponent as TagIcon } from 'assets/tag-icon.svg';
import { ReactComponent as TextIcon } from 'assets/text-icon.svg';
import { ReactComponent as TypingIcon } from 'assets/typing-icon.svg';
import { ReactComponent as VideoIcon } from 'assets/video-icon.svg';
import { ReactComponent as QqEmail } from 'assets/widget-icons/qq_email.svg';
import { ReactComponent as QqInfo } from 'assets/widget-icons/qq_info.svg';
import { ReactComponent as QqOptions } from 'assets/widget-icons/qq_options.svg';
import { ReactComponent as QqPhone } from 'assets/widget-icons/qq_phone.svg';
import classnames from 'classnames';
import { kebabCase } from 'lodash';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { WidgetPlatforms } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetPlatforms/WidgetPlatforms';
import { useIsLeadGeneratorBuilderRoute } from '../../../modules/FreeLeadGenerator/use-is-lead-generator-builder-route';
import { useAttachmentWidgetHandlers } from '../../helpers/widget-helpers/use-attachment-widget-handlers';
import { useAttributeWidgetHandlers } from '../../helpers/widget-helpers/use-attribute-widget-handlers';
import { useAudioWidgetHandlers } from '../../helpers/widget-helpers/use-audio-widget-handlers';
import { useConnectionWidgetHandlers } from '../../helpers/widget-helpers/use-connection-widget-handlers';
import { useDelayWidgetHandlers } from '../../helpers/widget-helpers/use-delay-widget-handlers';
import { usePushNotificationWidgetHanlders } from '../../helpers/widget-helpers/use-email-widget-handlers';
import { useFormWidgetHandlers } from '../../helpers/widget-helpers/use-form-widget-handlers';
import { useGalleryWidgetHandlers } from '../../helpers/widget-helpers/use-gallery-widget-handlers';
import { useGifWidgetHandlers } from '../../helpers/widget-helpers/use-gif-widget-handlers';
import { useImageWidgetHandlers } from '../../helpers/widget-helpers/use-image-widget-handlers';
import { useNavigateWidgetHandlers } from '../../helpers/widget-helpers/use-navigate-widget-handlers';
import { useOptInWidgetHandlers } from '../../helpers/widget-helpers/use-opt-in-widget-handlers';
import { useOtnWidgetHandlers } from '../../helpers/widget-helpers/use-otn-widget-handlers';
import { useQQWidgetHandlers } from '../../helpers/widget-helpers/use-qq-widget-handlers';
import { useSmsOptinWidgetHandlers } from '../../helpers/widget-helpers/use-sms-optin-widget-handlers';
import { useTagsWidgetHandlers } from '../../helpers/widget-helpers/use-tags-widget-handlers';
import { useTextWidgetHandlers } from '../../helpers/widget-helpers/use-text-widget-handlers';
import { useTypingWidgetHandlers } from '../../helpers/widget-helpers/use-typing-widget-handlers';
import { useVideoWidgetHandlers } from '../../helpers/widget-helpers/use-video-widget-handlers';
import { useGreenCueMarkers } from '../../hooks/use-green-cue-markers';
import { useIsOnboardingRouteCheck } from '../../hooks/use-is-onboarding-route-check';
import { GreenMarker } from '../GreenMarker/GreenMarker';
import { OnClickElsewhere } from '../OnClickElsewhere/OnClickElsewhere';
import { PlanBadge } from '../ProBadge';
import './AddWidgetInsert.pcss';

export type AddWidgetProps = {
  dialogueId: number;
  sortOrder: number;
  allowedWidgets?: WidgetTypes[];
  widgetsToExclude?: WidgetTypes[];
  isLastChild?: boolean;
  isSmsRelated?: boolean;
  setIsHovered: (isHovered: boolean) => void;
};

const FREQUENT_USED_WIDGET_TYPES = [
  WidgetTypes.TEXT,
  WidgetTypes.QQ_OPTIONS,
  WidgetTypes.QQ_EMAIL,
  WidgetTypes.QQ_PHONE,
  WidgetTypes.IMAGE,
];

export const QQ_SPLIT_ALERT =
  'Quick Question widget transformed into: Options, Ask for Email, Ask for Phone, Ask for Info';

type OnClickType = (
  dialogueId: number,
  sortOrder: number,
  ...args: any[]
) => Promise<WidgetModel | undefined>;

export type WidgetDefinition = {
  type: WidgetTypes;
  name: string;
  icon: FunctionComponent<any>;
  onClick: OnClickType;
  className: string;
  requiredFeature?: SubscriptionFeatureFlag;
};

export const useSupportedWidgets = (): {
  mmWidgets: WidgetDefinition[];
  connectionWidgets: WidgetDefinition[];
  allWidgets: WidgetDefinition[];
} => {
  const qqWidgetHandlers = useQQWidgetHandlers();
  const imageWidgetHandlers = useImageWidgetHandlers();
  const textWidgetHandlers = useTextWidgetHandlers();
  const notificationWidget = usePushNotificationWidgetHanlders();
  const attachmentWidgetHandlers = useAttachmentWidgetHandlers();
  const gifWidgetHandlers = useGifWidgetHandlers();
  const typingWidgetHandlers = useTypingWidgetHandlers();
  const tagsWidgetHandlers = useTagsWidgetHandlers();
  const videoWidgetHandlers = useVideoWidgetHandlers();
  const audioWidgetHandlers = useAudioWidgetHandlers();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const optInWidgetHandlers = useOptInWidgetHandlers();
  const attributeWidgetHandlers = useAttributeWidgetHandlers();
  const galleryWidgetHandlers = useGalleryWidgetHandlers();
  const navigateWidgetHandlers = useNavigateWidgetHandlers();
  const otnWidgetHandlers = useOtnWidgetHandlers();
  const smsOptinWidgetHandler = useSmsOptinWidgetHandlers();
  const formWidgetHandlers = useFormWidgetHandlers();
  const delayWidgetHandlers = useDelayWidgetHandlers();

  const twilioFeatFlag = useSelector(companyFeatureFlagsSelector).twilio;
  const twilioAvailableFlag = useSelector(getTwilioAvailable);
  const isSmsSupported = twilioFeatFlag || twilioAvailableFlag;
  const session = useSelector(selectSessionSelector);

  const { isOnboardingRoute } = useIsOnboardingRouteCheck();

  const base = [
    {
      type: WidgetTypes.QQ_OPTIONS,
      name: 'Options',
      icon: QqOptions,
      onClick: qqWidgetHandlers.addBaseQuestion,
      className: 'widget-add-item--type-quick-question',
    },
    {
      type: WidgetTypes.QQ_EMAIL,
      name: 'Ask for Email',
      icon: QqEmail,
      onClick: qqWidgetHandlers.addEmailQuestion,
      className: 'widget-add-item--type-quick-question',
    },
    {
      type: WidgetTypes.QQ_PHONE,
      name: 'Ask for Phone',
      icon: QqPhone,
      onClick: qqWidgetHandlers.addPhoneQuestion,
      className: 'widget-add-item--type-quick-question',
    },
    {
      type: WidgetTypes.QQ_INFO,
      name: 'Ask for Info',
      icon: QqInfo,
      onClick: qqWidgetHandlers.addInfoQuestion,
      className: 'widget-add-item--type-quick-question',
    },
    {
      type: WidgetTypes.IMAGE,
      name: 'Image',
      icon: ImageUploadIcon,
      onClick: imageWidgetHandlers.addNew,
      className: 'widget-add-item--type-image',
    },
  ];

  const isLeadGeneratorRoute = useIsLeadGeneratorBuilderRoute();

  const mmWidgets = [
    ...base,
    {
      type: WidgetTypes.DELAY,
      name: 'Delay',
      icon: DelayIcon,
      onClick: delayWidgetHandlers.addNew,
      className: 'widget-add-item--type-delay',
      requiredFeature: 'delayWidget' as const,
    },
    {
      type: WidgetTypes.TEXT,
      name: 'Text',
      icon: TextIcon,
      onClick: textWidgetHandlers.addNew,
      className: 'widget-add-item--type-text',
    },
    {
      type: WidgetTypes.FORM,
      name: 'Form',
      icon: FormIcon,
      onClick: formWidgetHandlers.addNew,
      className: 'widget-add-item--type-form',
    },
    {
      type: WidgetTypes.GALLERY,
      name: 'Gallery',
      icon: GalleryIcon,
      onClick: galleryWidgetHandlers.addNew,
      className: 'widget-add-item--type-gallery',
    },
    ...(!isOnboardingRoute
      ? [
          {
            type: WidgetTypes.EMAIL,
            name: 'Notification',
            icon: NotificationIcon,
            onClick: (dialogueId: number, sortOrder: number) =>
              notificationWidget.addNew(dialogueId, sortOrder, session?.email),
            className: 'widget-add-item--type-email',
            requiredFeature: 'notificationWidget' as const,
          },
        ]
      : []),
    {
      type: WidgetTypes.ATTACHMENT,
      name: 'Attachment',
      icon: AttachmentIcon,
      onClick: attachmentWidgetHandlers.addNew,
      className: 'widget-add-item--type-attachment',
    },
    {
      type: WidgetTypes.AUDIO,
      name: 'Audio',
      icon: AudioIcon,
      onClick: audioWidgetHandlers.addNew,
      className: 'widget-add-item--type-audio',
    },
    {
      type: WidgetTypes.GIF,
      name: 'Gif',
      icon: GifIcon,
      onClick: gifWidgetHandlers.addNew,
      className: 'widget-add-item--type-gif',
    },
    {
      type: WidgetTypes.NAVIGATE,
      name: 'Navigate',
      icon: NavigateIcon,
      onClick: (dialogueId: number, sortOrder: number) =>
        navigateWidgetHandlers.addNavigate({ dialogueId, sortOrder }),
      className: 'widget-add-item--type-navigate',
    },
    {
      type: WidgetTypes.TYPING,
      name: 'Typing',
      icon: TypingIcon,
      onClick: typingWidgetHandlers.addNew,
      className: 'widget-add-item--type-typing',
    },
    ...(!isOnboardingRoute
      ? [
          {
            type: WidgetTypes.ATTRIBUTE,
            name: 'Attribute',
            icon: AttributeIcon,
            onClick: attributeWidgetHandlers.addNew,
            className: 'widget-add-item--type-attribute',
          },
        ]
      : []),
    {
      type: WidgetTypes.TAGS,
      name: 'Tags',
      icon: TagIcon,
      onClick: tagsWidgetHandlers.addNew,
      className: 'widget-add-item--type-tags',
    },
    {
      type: WidgetTypes.OTN,
      name: '1-Time Notification',
      icon: OtnIcon,
      onClick: otnWidgetHandlers.addNew,
      className: 'widget-add-item--type-otn',
    },
    {
      type: WidgetTypes.VIDEO,
      name: 'Video',
      icon: VideoIcon,
      onClick: videoWidgetHandlers.addNew,
      className: 'widget-add-item--type-video',
    },
    {
      type: WidgetTypes.OPT_IN,
      name: 'Messenger opt-in',
      icon: SmsOptInIcon,
      onClick: optInWidgetHandlers.addNew,
      className: 'widget-add-item--type-opt-in',
    },
    // ...(isSmsSupported
    //   ? [
    //       {
    //         type: WidgetTypes.SMS_OPT_IN_V2,
    //         name: 'SMS Opt-in',
    //         icon: SmsOptInIcon,
    //         onClick: smsOptinWidgetHandler.addNew,
    //         className: 'widget-add-item--type-sms-opt-in',
    //       },
    //     ]
    //   : []),
  ];

  const connectionWidgets = [
    ...[
      {
        type: WidgetTypes.SEND_DATA,
        name: 'Google Sheets',
        icon: GoogleSheetsIcon,
        onClick: (dialogueId: number, sortOrder: number) =>
          connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'google_sheets',
          ),
        className: 'widget-add-item--type-google-sheets',
        requiredFeature: 'nativeIntegrations' as const,
      },
    ],
    ...(!isOnboardingRoute
      ? [
          {
            type: WidgetTypes.SEND_DATA,
            name: 'Zap/ Connection',
            icon: ConnectionIcon,
            onClick: (dialogueId: number, sortOrder: number) =>
              connectionWidgetHandlers.addNew(dialogueId, sortOrder, 'zapier'),
            className: 'widget-add-item-type-send-data',
            requiredFeature: 'connectionWidget' as const,
          },
        ]
      : []),
  ];

  if (isLeadGeneratorRoute) {
    return {
      mmWidgets: base,
      connectionWidgets,
      allWidgets: [...base, ...connectionWidgets],
    };
  }

  return {
    mmWidgets,
    connectionWidgets,
    allWidgets: [...mmWidgets, ...connectionWidgets],
  };
};

type WidgetCreatorHandlerType = (
  dialogueId: number,
  sortOrder: number,
  isSmsRelated?: boolean,
  body?: string,
  allowFreeTextResponse?: boolean,
) => any;

type WidgetAddItemProps = {
  requiredFeature?: SubscriptionFeatureFlag;
  dialogueId: number;
  sortOrder: number;
  name: string;
  icon: FunctionComponent<any>;
  className?: string;
  isSmsRelated?: boolean;
  onClick: WidgetCreatorHandlerType;
  onClickEnd?: () => void;
  type: WidgetTypes;
  withChannelValidation?: boolean;
  iconOnly?: boolean;
};

export const WidgetAddItem = (props: WidgetAddItemProps) => {
  const {
    name,
    icon: Icon,
    onClick,
    dialogueId,
    sortOrder,
    className,
    onClickEnd = () => {},
    requiredFeature,
    isSmsRelated,
    type,
    withChannelValidation,
    iconOnly,
  } = props;
  const dispatch = useDispatch();
  const widgetPlatformsMap = useSelector(selectWidgetPlatformsMap) || {};
  const typeWithCorrectQQType = [
    WidgetTypes.QQ_OPTIONS,
    WidgetTypes.QQ_EMAIL,
    WidgetTypes.QQ_PHONE,
    WidgetTypes.QQ_INFO,
  ].includes(type)
    ? WidgetTypes.QUICK_QUESTION
    : type;
  const platforms = widgetPlatformsMap[typeWithCorrectQQType];

  const billingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, requiredFeature),
  );

  const upgradePlan = getPlanFromBillingType(billingType);

  const showUpgradeModal = () =>
    dispatch(
      openUniversalUpgradeLandmineModal({
        landmineName: `${
          landmineNames['STARTBOTS-DIALOGUE-EDITOR']
        }__ADD-WIDGET-INSERT__WIGET__${kebabCase(name).toUpperCase()}`,
        feature: requiredFeature,
      }),
    );

  const featureFlags = useSelector(combinedFeatureFlagsSelector);

  const onClickAddItem = () => {
    const isRequiredFeatureAvailable =
      !!requiredFeature && !!featureFlags[requiredFeature];

    const isUpgradeNeeded = !isRequiredFeatureAvailable && !!upgradePlan;

    if (isUpgradeNeeded) {
      showUpgradeModal();
      return;
    }
    onClick(dialogueId, sortOrder, isSmsRelated, '');
    onClickEnd();
  };

  const { isNotificationWidgetActivated } = useGreenCueMarkers();

  const showWidgetCue =
    type === WidgetTypes.EMAIL && !isNotificationWidgetActivated;

  const classNameComputer = classnames('widget-add-item', className, {
    'js-quick-question': [
      WidgetTypes.QUICK_QUESTION,
      WidgetTypes.QQ_OPTIONS,
      WidgetTypes.QQ_EMAIL,
      WidgetTypes.QQ_PHONE,
      WidgetTypes.QQ_INFO,
    ].includes(type),
  });

  return (
    <div
      className={classnames('widget-add-item__wrapper', {
        'widget-add-item__wrapper--with-validation': withChannelValidation,
        'widget-add-item__wrapper--icon-only': iconOnly,
      })}
    >
      {showWidgetCue && <GreenMarker className="widget-add-item__cue" />}
      <div className={classNameComputer} onClick={onClickAddItem}>
        <span className="widget-add-item__icon-container">
          <Icon className="widget-add-item__icon" />
        </span>

        {billingType && (
          <PlanBadge
            className="widget-add-item__pro_badge"
            billingType={billingType}
          />
        )}

        {withChannelValidation && platforms && (
          <div className="widget-add-item__platforms">
            <WidgetPlatforms platforms={platforms} />
          </div>
        )}

        <div className="widget-add-item__name">{name}</div>
      </div>
    </div>
  );
};

const sortByOrder = <T extends { [index: string]: any }>(
  arr: T[],
  order: any[],
  key: string,
): T[] => {
  return arr.sort((a, b) => {
    return order.indexOf(a[key]) > order.indexOf(b[key]) ? 1 : -1;
  });
};

export const AddWidgetInsert = (props: AddWidgetProps) => {
  const {
    sortOrder,
    dialogueId,
    allowedWidgets,
    isLastChild = false,
    setIsHovered,
    isSmsRelated,
    widgetsToExclude,
  } = props;

  const { updateUserAttributes } = useUsersCRUDActions();
  const { userAttributes } = useSelector(selectSessionSelector);
  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);

  const { isNotificationWidgetActivated } = useGreenCueMarkers();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { mmWidgets, connectionWidgets } = useSupportedWidgets();

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const filteredMmWidgets =
    widgetsToExclude && widgetsToExclude.length > 0
      ? mmWidgets.filter(w => !widgetsToExclude.includes(w.type))
      : allowedWidgets && allowedWidgets.length > 0
      ? mmWidgets.filter(w => allowedWidgets.includes(w.type))
      : mmWidgets;

  const filteredConnectionWidgets =
    widgetsToExclude && widgetsToExclude.length > 0
      ? connectionWidgets.filter(w => !widgetsToExclude.includes(w.type))
      : allowedWidgets && allowedWidgets.length > 0
      ? connectionWidgets.filter(w => allowedWidgets.includes(w.type))
      : connectionWidgets;

  const frequentUsedWidgets = filteredMmWidgets.filter(({ type }) =>
    FREQUENT_USED_WIDGET_TYPES.includes(type),
  );

  const frequentUsedWidgetsOrdered = sortByOrder(
    frequentUsedWidgets,
    FREQUENT_USED_WIDGET_TYPES,
    'type',
  );

  const onClickOutsideDropdown = () => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    }
  };

  const [slideoutActive, setSlideoutActive] = useState(false);
  const dispatch: Dispatch<StoreState> = useDispatch();
  const onMoreButtonClick = () => {
    onClickOutsideDropdown();
    setSlideoutActive(true);
    dispatch(
      showSlideoutMenu('widgetAdder', {
        ...props,
        mmWidgets: filteredMmWidgets,
        connectionWidgets: filteredConnectionWidgets,
        onSlideoutClose: () => setSlideoutActive(false),
      }),
    );
  };

  const isLeadGeneratorRoute = useIsLeadGeneratorBuilderRoute();

  const hoverOn = () => setIsHovered(true);
  const hoverOff = () => setIsHovered(false);
  const setDropdown = () => {
    if (!slideoutActive) {
      setDropdownOpen(true);
      setTimeout(
        () =>
          dropdownRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          }),
        100,
      );
    }
  };

  const ref = useRef<HTMLDivElement | null>(null);

  const onAddWidgetClick = () => {
    setDropdown();
  };

  return (
    <div
      ref={ref}
      className={classnames('add-widget-insert', {
        'add-widget-insert--dropdown-open': dropdownOpen,
        'add-widget-insert--last': isLastChild,
        'add-widget-insert--slideout-open': slideoutActive,
      })}
      onMouseEnter={hoverOn}
      onMouseLeave={hoverOff}
    >
      <div className="add-widget-insert__wrapper" onClick={onAddWidgetClick}>
        <div className="add-widget-insert__line" />

        <div className="add-widget-insert__add-btn">
          {!isNotificationWidgetActivated && (
            <GreenMarker className="add-widget-insert__cue" />
          )}
          <CirclePlusIcon className="add-widget-insert__add-icon" />

          <span className="add-widget-insert__add-text">Add widget</span>
        </div>

        <div className="add-widget-insert__helper-text">
          Select a widget from the list on the right to add it here
        </div>

        <div className="add-widget-insert__line" />
      </div>

      {dropdownOpen && (
        <OnClickElsewhere
          handleClick={onClickOutsideDropdown}
          className="add-widget-insert__dropdown"
        >
          <div
            className="add-widget-insert__dropdown-content"
            ref={dropdownRef}
          >
            <span className="add-widget-insert__dropdown-top-arrow" />
            <div className="add-widget-insert__dropdown-content--container">
              <div className="add-widget-insert__dropdown-section">
                <div className="add-widget-insert__dropdown-widgets">
                  {frequentUsedWidgetsOrdered.map(
                    ({
                      type,
                      name,
                      icon,
                      onClick,
                      className,
                      requiredFeature,
                    }) => (
                      <WidgetAddItem
                        key={type}
                        name={name}
                        icon={icon}
                        onClick={onClick}
                        onClickEnd={() => setDropdownOpen(false)}
                        sortOrder={sortOrder}
                        dialogueId={dialogueId}
                        className={className}
                        isSmsRelated={isSmsRelated}
                        type={type}
                        requiredFeature={requiredFeature}
                      />
                    ),
                  )}
                </div>

                {!isLeadGeneratorRoute && (
                  <div
                    className="add-widget-insert__more-btn"
                    onClick={onMoreButtonClick}
                  >
                    <HelpText
                      className="add-widget-insert__more-btn-text"
                      text="More widgets"
                    />
                  </div>
                )}
              </div>

              {!userAttributes.seenQqSplitAlert && (
                <div className="add-widget-insert__dropdown-content--alert">
                  <Alert
                    type="info"
                    closeIconProps={{
                      onClick: () =>
                        updateUserAttributes(companyId, currentUserId, {
                          seenQqSplitAlert: true,
                        }),
                    }}
                  >
                    {QQ_SPLIT_ALERT}
                  </Alert>
                </div>
              )}
            </div>
          </div>
        </OnClickElsewhere>
      )}
    </div>
  );
};
